import { useCallback, useRef, useEffect } from 'react';

const useBridgeTransaction = () => {
  const iframeRef = useRef(null);
  const pendingRequests = useRef(new Map());

  useEffect(() => {
    // Create hidden iframe on mount
    const iframe = document.createElement('iframe');
    iframe.src = '/bridge/'; // URL where your bridge is served
    iframe.style.display = 'none'; // start hidden
    iframe.style.position = 'fixed';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100vw';
    iframe.style.height = '100vh';
    iframe.style.border = 'none';
    iframe.style.zIndex = '9999';
    iframe.style.pointerEvents = 'none'; // allow clicks to pass through when hidden
    document.body.appendChild(iframe);
    iframeRef.current = iframe;

    return () => {
      if (iframeRef.current) document.body.removeChild(iframeRef.current);
    };
  }, []);

  const sendTransaction = useCallback(async (tx, timeoutMs = 60000) => {
    const requestId = `tx-${Date.now()}-${Math.random().toString(36)}`;

    // Show iframe and enable pointer events
    if (iframeRef.current) {
      iframeRef.current.style.display = 'block';
      iframeRef.current.style.pointerEvents = 'auto';
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handler);
        pendingRequests.current.delete(requestId);
        // Hide iframe again
        if (iframeRef.current) {
          iframeRef.current.style.display = 'none';
          iframeRef.current.style.pointerEvents = 'none';
        }
        reject(new Error('Transaction request timed out'));
      }, timeoutMs);

      const handler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type === 'BRIDGE_TRANSACTION_RESULT' && event.data.requestId === requestId) {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          const { result, error } = event.data;
          // Hide iframe again
          if (iframeRef.current) {
            iframeRef.current.style.display = 'none';
            iframeRef.current.style.pointerEvents = 'none';
          }
          if (error) reject(new Error(error));
          else resolve(result);
          pendingRequests.current.delete(requestId);
        }
      };
      window.addEventListener('message', handler);
      pendingRequests.current.set(requestId, { resolve, reject });

      if (!iframeRef.current?.contentWindow) {
        reject(new Error('Bridge iframe not ready'));
        return;
      }

      iframeRef.current.contentWindow.postMessage({
        type: 'BRIDGE_SIGN_TRANSACTION',
        requestId,
        data: tx
      }, '*');
    });
  }, []);

  return { sendTransaction };
};

export default useBridgeTransaction;
